/**
 * i18n resources for the `calls` feature slice (master prompt §6/§7/§13).
 *
 * Namespace: `calls`. Every user-facing string in the calls/live-calls/call
 * detail/conversations pages and their subcomponents lives here. `bg`, `de`,
 * and `fr` currently mirror `en` (translation follow-up) — they exist so the
 * key-parity assertion and `useT('calls')` resolve in every supported locale.
 *
 * The coordinator merges this namespace into the platform i18n aggregator.
 */

const en = {
  history: {
    eyebrow: 'Reception',
    title: 'Call history',
    description: 'Every call your AI receptionist has handled, newest first.',
    columns: {
      status: 'Status',
      language: 'Language',
      direction: 'Direction',
      duration: 'Duration',
      startedAt: 'Started',
    },
    filters: {
      label: 'Show',
      all: 'All calls',
      active: 'Active only',
      completed: 'Completed only',
    },
    limit: {
      label: 'Rows',
    },
    rowAction: 'View call',
    toolbar: {
      searchPlaceholder: 'Search by call id',
      statusAll: 'All statuses',
      directionAll: 'All directions',
      languagePlaceholder: 'Language',
      fromLabel: 'From',
      toLabel: 'To',
      clear: 'Clear filters',
    },
    pagination: {
      prev: 'Previous',
      next: 'Next',
      perPage: 'Rows per page',
      range: '{{from}}–{{to}} of {{total}}',
    },
    empty: {
      title: 'No calls yet',
      description: 'Calls answered by your receptionist will appear here.',
    },
    emptyFiltered: {
      title: 'No matching calls',
      description: 'No calls match these filters. Try clearing them.',
    },
    error: {
      title: 'Could not load calls',
      description: 'Something interrupted the call history. Please try again.',
    },
    loading: 'Loading calls',
  },
  live: {
    eyebrow: 'Reception',
    title: 'Live calls',
    description: 'Calls happening right now, with the transcript as it unfolds.',
    liveNow: '{{count}} live now',
    idle: 'No calls in progress',
    liveBadge: 'Live',
    elapsedLabel: 'On call for {{time}}',
    activeList: {
      title: 'Active now',
      empty: {
        title: 'No calls yet',
        description:
          'When a guest is on the line, the call appears here — recent calls show below.',
      },
    },
    recentList: {
      title: 'Recent',
    },
    status: {
      created: 'Connecting',
      ringing: 'Ringing',
      answered: 'Answered',
      ai_active: 'AI speaking',
      handoff_requested: 'Handoff requested',
      human_active: 'Staff on call',
    },
    outcome: {
      completed: 'Completed',
      failed: 'Failed',
    },
    recentDetail: {
      language: 'Language',
      direction: 'Direction',
      started: 'Started',
      ended: 'Ended',
      endReason: 'End reason',
      viewFull: 'View full details',
    },
    panel: {
      caller: 'Caller',
      language: 'Language',
      aiStatus: 'AI status',
      selectPrompt: {
        title: 'Select a call',
        description: 'Choose an active call to follow its transcript live.',
      },
    },
    transcript: {
      title: 'Transcript',
    },
    draft: {
      title: 'Draft reservation',
      placeholder: 'A reservation draft will appear here once the AI captures intent.',
    },
    handoff: {
      button: 'Request human handoff',
      requesting: 'Requesting…',
      requested: 'Handoff requested',
      error: 'Could not request handoff. Please try again.',
      unavailable: 'Handoff is only available while a call is active.',
    },
    audio: {
      title: 'Audio bridge',
      states: {
        forbidden: {
          title: 'Audio bridge unavailable',
          body: 'You do not have permission to join calls by audio.',
        },
        pending: {
          title: 'Audio bridge on standby',
          body: 'The audio bridge becomes available once a handoff is in progress.',
        },
        requesting: {
          title: 'Preparing audio bridge',
          body: 'Requesting a secure connection to join this call.',
        },
        unavailable: {
          title: 'Audio bridge not ready',
          body: 'The audio bridge will be available once the backend connection service is live.',
        },
        connecting: {
          title: 'Connecting',
          body: 'Joining the call audio. This takes a moment.',
        },
        connected: {
          title: 'Connected to call audio',
          body: 'You are on the call. Use the controls to mute or leave.',
        },
        tokenDenied: {
          title: 'Could not join — access denied',
          body: 'The connection was refused. The call may have ended or moved on.',
        },
        micDenied: {
          title: 'Microphone access denied',
          body: 'Allow microphone access in your browser to speak on the call.',
        },
        disconnected: {
          title: 'Disconnected from call audio',
          body: 'The audio connection ended. The call may have completed.',
        },
        failed: {
          title: 'Audio connection failed',
          body: 'Something interrupted the audio connection. Please try again.',
        },
      },
    },
  },
  detail: {
    eyebrow: 'Reception',
    title: 'Call detail',
    backToCalls: 'Back to calls',
    fields: {
      callId: 'Call ID',
      status: 'Status',
      language: 'Language',
      direction: 'Direction',
      duration: 'Duration',
      startedAt: 'Started',
      endedAt: 'Ended',
      endReason: 'End reason',
    },
    transcript: {
      title: 'Transcript',
      empty: {
        title: 'No transcript',
        description: 'This call has no transcript segments.',
      },
    },
    summary: {
      title: 'Summary',
      notReady: {
        title: 'Summary not ready',
        description: 'The post-call summary is generated once the call is processed.',
      },
    },
    tools: {
      title: 'Tool activity',
      empty: {
        title: 'No tool activity',
        description: 'Decisions the AI took during the call will appear here.',
      },
      names: {
        flag_handoff: 'Requested staff handoff',
        create_reservation_request: 'Created reservation request',
      },
      outcome: {
        executed: 'Executed',
        denied: 'Denied',
        failed: 'Failed',
        idempotent: 'No change',
        in_flight: 'In progress',
      },
    },
    recording: {
      title: 'Recording',
      placeholder: 'Recording playback will be available when recording is enabled.',
    },
    loading: 'Loading call',
    error: {
      title: 'Could not load call',
      description: 'Something interrupted this call. Please try again.',
    },
    notFound: {
      title: 'Call not found',
      description: 'This call is not available in this hotel.',
    },
  },
  conversations: {
    eyebrow: 'Reception',
    title: 'Conversations',
    description: 'Cross-channel guest conversations, unified across voice and chat.',
    empty: {
      title: 'No conversations yet',
      description:
        'Voice, chat, and messaging conversations will gather here as channels are connected.',
    },
  },
  status: {
    created: 'Created',
    ringing: 'Ringing',
    answered: 'Answered',
    ai_active: 'AI active',
    handoff_requested: 'Handoff requested',
    human_active: 'Staff on call',
    completed: 'Completed',
    failed: 'Failed',
    unknown: 'Unknown',
  },
  direction: {
    inbound: 'Inbound',
    outbound: 'Outbound',
  },
  speaker: {
    guest: 'Guest',
    agent: 'AI receptionist',
    staff: 'Staff',
    system: 'System',
  },
  common: {
    notAvailable: '—',
  },
} as const;

export const i18n = {
  namespace: 'calls',
  resources: {
    en,
    bg: en,
    de: en,
    fr: en,
  },
} as const;

export default i18n;
